// EntityBindingSelect — выбор привязки платежа к сущности (V12 U-35, волна R66).
// Паттерн (постановка): селект типа (Компания/Контакт/Сделка/Лид) + Autocomplete
// сущности выбранного типа («тип → выбери сущность через соответствующую автокомплиту»).
// Используется PaymentNewDialog (manual) и ReceiptUploadDialog (загрузка чека).

import { useQuery } from '@tanstack/react-query';
import { Autocomplete, MenuItem, Stack, TextField, type TextFieldProps } from '@mui/material';
import { companiesApi } from '@/api/companies';
import { contactsApi } from '@/api/contacts';
import { dealsApi } from '@/api/deals';
import { leadsApi } from '@/api/leads';
import { t } from '@/i18n';
import type { PaymentEntityType } from '@/api/payments';

/** Привязка платежа к одной сущности (ровно одна из 4 типов). */
export interface PaymentBinding {
  entityType: PaymentEntityType;
  entityId: string;
}

/** Вариант сущности в автокомплите (название, а не id — U-15). */
interface EntityOption {
  id: string;
  label: string;
}

/** Порядок и подписи типов привязки (i18n key — payments.entityType.<TYPE>). */
const ENTITY_TYPES: PaymentEntityType[] = ['COMPANY', 'CONTACT', 'DEAL', 'LEAD'];

/** Сколько сущностей одного типа запрашиваем для автокомплита (backend size ≤ 100). */
const OPTIONS_PAGE_SIZE = 100;

/** staleTime для справочников сущностей (меняются редко). */
const OPTIONS_STALE_TIME_MS = 5 * 60 * 1000;

/** HTTP 403 — нет прав на сущность; показываем id вместо пустого label. */
const HTTP_FORBIDDEN = 403;

/** HTTP 404 — сущность не найдена; показываем id вместо пустого label. */
const HTTP_NOT_FOUND = 404;

/** Локализованная подпись типа привязки. */
export function formatPaymentEntityType(type: PaymentEntityType, translate: typeof t = t): string {
  return translate(`payments.entityType.${type}`);
}

function contactLabel(firstName: string, lastName: string): string {
  return `${firstName} ${lastName}`.trim();
}

function findOption(options: EntityOption[], id: string): EntityOption | undefined {
  return options.find((option) => option.id === id);
}

function withSelectedOption(options: EntityOption[], selected: EntityOption | null): EntityOption[] {
  if (!selected || findOption(options, selected.id)) {
    return options;
  }
  return [...options, selected];
}

/** Загружает варианты сущностей выбранного типа (KISS: первые 100 — список без q). */
function useEntityOptions(entityType: PaymentEntityType | null): {
  options: EntityOption[];
  isLoading: boolean;
} {
  const query = useQuery({
    queryKey: ['entity-options', entityType],
    queryFn: async (): Promise<EntityOption[]> => {
      switch (entityType) {
        case 'COMPANY': {
          const page = await companiesApi.list({ page: 0, size: OPTIONS_PAGE_SIZE });
          return page.content.map((c) => ({ id: c.id, label: c.name }));
        }
        case 'CONTACT': {
          const page = await contactsApi.list({ page: 0, size: OPTIONS_PAGE_SIZE });
          return page.content.map((c) => ({ id: c.id, label: contactLabel(c.firstName, c.lastName) }));
        }
        case 'DEAL': {
          const page = await dealsApi.list({ page: 0, size: OPTIONS_PAGE_SIZE });
          return page.content.map((d) => ({ id: d.id, label: d.title }));
        }
        case 'LEAD': {
          const page = await leadsApi.list({ page: 0, size: OPTIONS_PAGE_SIZE });
          return page.content.map((l) => ({ id: l.id, label: l.title }));
        }
        default:
          return [];
      }
    },
    enabled: entityType !== null,
    staleTime: OPTIONS_STALE_TIME_MS,
    retry: false,
  });
  return { options: query.data ?? [], isLoading: query.isLoading };
}

/** HTTP-статус axios-ошибки, если он есть. */
function httpStatus(error: unknown): number | undefined {
  if (typeof error !== 'object' || error === null || !('response' in error)) {
    return undefined;
  }
  const response = (error as { response?: { status?: number } }).response;
  return response?.status;
}

/**
 * Загружает подпись одной сущности по id (locked-селект вне первых 100).
 * 403/404 — контракт ТЗ: показать id, не пустое поле.
 */
async function fetchEntityLabel(entityType: PaymentEntityType, entityId: string): Promise<EntityOption> {
  try {
    switch (entityType) {
      case 'COMPANY': {
        const company = await companiesApi.get(entityId);
        return { id: company.id, label: company.name };
      }
      case 'CONTACT': {
        const contact = await contactsApi.get(entityId);
        return { id: contact.id, label: contactLabel(contact.firstName, contact.lastName) };
      }
      case 'DEAL': {
        const deal = await dealsApi.get(entityId);
        return { id: deal.id, label: deal.title };
      }
      case 'LEAD': {
        const lead = await leadsApi.get(entityId);
        return { id: lead.id, label: lead.title };
      }
    }
  } catch (error) {
    const status = httpStatus(error);
    if (status === HTTP_FORBIDDEN || status === HTTP_NOT_FOUND) {
      return { id: entityId, label: entityId };
    }
    throw error;
  }
}

/**
 * GET по id только если селект locked и сущности нет в list-options.
 * Unlocked автокомплит get-by-id не вызывает.
 */
function useLockedEntityLabel(
  locked: boolean,
  value: PaymentBinding | null,
  options: EntityOption[],
  optionsLoading: boolean,
): EntityOption | null {
  const entityType = value?.entityType ?? null;
  const entityId = value?.entityId ?? '';
  const missingFromOptions = Boolean(entityId) && findOption(options, entityId) === undefined;
  const enabled = locked && entityType !== null && missingFromOptions && !optionsLoading;

  const query = useQuery({
    queryKey: ['entity-label', entityType, entityId],
    queryFn: (): Promise<EntityOption> => {
      if (entityType === null || entityId === '') {
        return Promise.resolve({ id: entityId, label: entityId });
      }
      return fetchEntityLabel(entityType, entityId);
    },
    enabled,
    staleTime: OPTIONS_STALE_TIME_MS,
    retry: false,
  });
  return query.data ?? null;
}

interface EntityBindingSelectProps {
  /** Текущая привязка (null — ничего не выбрано). */
  value: PaymentBinding | null;
  /** Колбэк смены привязки (null — сброшено). */
  onChange: (binding: PaymentBinding | null) => void;
  /** Заблокировать выбор (предзаполненная привязка из карточки/блока). */
  locked?: boolean;
}

/** Селект типа + автокомплит сущности для привязки платежа. */
export function EntityBindingSelect({ value, onChange, locked = false }: EntityBindingSelectProps): JSX.Element {
  const { options, isLoading } = useEntityOptions(value?.entityType ?? null);
  const lockedEntityOption = useLockedEntityLabel(locked, value, options, isLoading);
  const selectedOption = value
    ? (findOption(options, value.entityId) ?? lockedEntityOption ?? null)
    : null;
  const autocompleteOptions = withSelectedOption(options, selectedOption);
  const typeLabel = value ? formatPaymentEntityType(value.entityType) : '';

  return (
    <Stack spacing={2}>
      <TextField
        select
        fullWidth
        label={t('payments.entityType.label')}
        value={value?.entityType ?? ''}
        disabled={locked}
        onChange={(e) => {
          const next = e.target.value as PaymentEntityType | '';
          onChange(next ? { entityType: next, entityId: '' } : null);
        }}
        inputProps={{ 'aria-label': t('payments.entityType.label') }}
      >
        {ENTITY_TYPES.map((type) => (
          <MenuItem key={type} value={type}>
            {formatPaymentEntityType(type)}
          </MenuItem>
        ))}
      </TextField>
      <Autocomplete
        value={selectedOption}
        options={autocompleteOptions}
        loading={isLoading}
        disabled={locked}
        getOptionLabel={(option: EntityOption) => option.label}
        isOptionEqualToValue={(option, current) => option.id === current.id}
        onChange={(_event, option) => {
          if (!value) return;
          onChange(option ? { entityType: value.entityType, entityId: option.id } : null);
        }}
        renderInput={(params) => (
          <TextField
            // AutocompleteRenderInputParams несёт явные undefined (size и др.), что под
            // exactOptionalPropertyTypes несовместимо с TextFieldProps — точечный cast (канон
            // DepartmentAutocomplete.tsx:80-84).
            {...(params as unknown as TextFieldProps)}
            fullWidth
            label={`${t('payments.entity')}: ${typeLabel}`}
            inputProps={{ ...params.inputProps, 'aria-label': t('payments.entity') }}
          />
        )}
      />
    </Stack>
  );
}
